import BaseComponent from '../BaseComponent';
import EventHandler from '../../utils/dom/event-handler';
import SelectorEngine from '../../utils/dom/selector-engine';

const NAME = 'DatePickerDisplay';

class DatePickerDisplay extends BaseComponent {
	format = "YYYY-MM-DD";
	constructor(element) {
		super(element);
		if (this._element) {
			this.init();
		}
	}

	static get NAME() {
		return NAME;
	}

	init() {
		this.datePicker = SelectorEngine.findOne("duet-date-picker", this._element);
		this.output = SelectorEngine.findOne(".lta-datepickerDisplay-label", this._element);
		const pickerValue = this.datePicker.getAttribute('value');
		const pickerMinValue = this.datePicker.getAttribute('min');
		const previousDate = SelectorEngine.findOne('.lta-datepickerDisplay__nav-icon-previous', this._element);

		const nextDate = SelectorEngine.findOne(".lta-datepickerDisplay__nav-icon-next", this._element);
		const changeByButton = new Event("btnchange", { "bubbles": true, "cancelable": false });

		if (pickerMinValue >= pickerValue) {
			previousDate.classList.add("isDisabled");
			previousDate.disabled = true;
		}

		let fullDate = new Date(pickerValue).toLocaleDateString('en-us', { weekday: "long", year: "numeric", month: "long", day: "numeric" });

		this.updateDisplayDate(new Date(this.datePicker.getAttribute('value')));

		EventHandler.on(this.datePicker, 'duetChange', (event) => {
			fullDate = event.detail.valueAsDate.toLocaleDateString('en-us', { weekday: "long", year: "numeric", month: "long", day: "numeric" });

			this.output.innerHTML = this.formatFullDate(fullDate);
			const fullDateFormat = event.detail.valueAsDate.toISOString().split('T')[0];
			if (fullDateFormat < pickerMinValue) {
				previousDate.classList.add("isDisabled");
				previousDate.disabled = true;
			} else {
				previousDate.classList.remove("isDisabled");
				previousDate.disabled = false;
			}
			this.datePicker.dispatchEvent(changeByButton);
		});

		EventHandler.on(nextDate, 'click', () => {
			const updatedPickerValue = this.datePicker.getAttribute('value');
			const date = new Date(updatedPickerValue);
			date.setDate(date.getDate() + 1);
			const dateFormat = date.toISOString().split('T')[0];
			this.datePicker.setAttribute('value', dateFormat);
			fullDate = new Date(dateFormat).toLocaleDateString('en-us', { weekday: "long", year: "numeric", month: "long", day: "numeric" });

			this.output.innerHTML = this.formatFullDate(fullDate);

			if (pickerMinValue - 1 >= updatedPickerValue) {
				previousDate.classList.add("isDisabled");
				previousDate.disabled = true;
			} else {
				previousDate.classList.remove("isDisabled");
				previousDate.disabled = false;
			}
			nextDate.dispatchEvent(changeByButton);
		});

		EventHandler.on(previousDate, 'click', () => {
			const updatedPickerValue = this.datePicker.getAttribute('value');
			const pickerNewMinValue = new Date(pickerMinValue);
			pickerNewMinValue.setDate(pickerNewMinValue.getDate() + 1);
			const pickerNewMinValueFormat = pickerNewMinValue.toISOString().split('T')[0];

			if (pickerNewMinValueFormat >= updatedPickerValue) {
				previousDate.classList.add("isDisabled");
				previousDate.disabled = true;
				this.previousDateFormat();
			} else {
				this.previousDateFormat();
				previousDate.classList.remove("isDisabled");
				previousDate.disabled = false;
			}
			previousDate.dispatchEvent(changeByButton);
		});

		window.customElements.whenDefined("duet-date-picker")
			.then(this.onDatepickerLoad.bind(this))
			.catch((error) => console.error(error));
	}

	onDatepickerLoad() {
		const interval = setInterval(() => {
			this.input = SelectorEngine.findOne('.duet-date__input', this._element);
			if (this.input) {
				clearInterval(interval);
				this.initDatepicker();
			}
		}, 500);
		return true;
	}

	initDatepicker() {
		this.DatePickerDisplay = SelectorEngine.findOne("duet-date-picker", this._element);
		this.calendarButton = SelectorEngine.findOne('.lta-datepickerDisplay__label-icon', this._element);

		// Making datepicker input component readonly.
		this.input.setAttribute('readonly', true);

		if (this.DatePickerDisplay) {
			// Open DatePickerDisplay when click / keyboard keypress event on input occurs.
			EventHandler.on(this.calendarButton, 'click', this.openDatePickerDisplay.bind(this));
			EventHandler.on(this.calendarButton, 'keypress', this.openDatePickerDisplay.bind(this));
		}
		this.dateParser();
		this.updateDisplayDate(new Date(this.datePicker.getAttribute('value')));
	}

	async dateParser() {
		const dayjs = await import('dayjs');
		const DATE_FORMAT = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
		const _this = this;

		this.datePicker.dateAdapter = {
			parse(value = '', createDate) {
				const matches = value.match(DATE_FORMAT);
				return matches ? createDate(matches[3], matches[2], matches[1]) : '';
			},
			format(date) {
				return dayjs.default(date).format(_this.format || 'YYYY-MM-DD');
			}
		};
	}

	dateSuffix(dateArraySplit) {
		const dString = String(dateArraySplit);
		const last = +dString.slice(-2);
		if (last > 3 && last < 21) return 'th';
		switch (last % 10) {
			case 1: return "st";
			case 2: return "nd";
			case 3: return "rd";
			default: return "th";
		}
	}

	formatFullDate(fullDate) {
		const dateArray = fullDate.split(", ");
		const dateArraySplit = dateArray[1];
		const sup = this.dateSuffix(dateArraySplit);
		dateArray[1] = dateArray[1] + sup;
		return dateArray.join(', ');
	}

	previousDateFormat() {
		// const picker = SelectorEngine.findOne("duet-date-picker", this._element);
		const updatedPickerValue = this.datePicker.getAttribute('value');
		const date = new Date(updatedPickerValue);
		date.setDate(date.getDate() - 1);
		this.updateDisplayDate(date);
	}

	updateDisplayDate(date) {
		const dateFormat = date.toISOString().split('T')[0];
		this.datePicker.setAttribute('value', dateFormat);

		const fullDate = new Date(dateFormat).toLocaleDateString('en-us', { weekday: "long", year: "numeric", month: "long", day: "numeric" });
		this.output.innerHTML = this.formatFullDate(fullDate);
	}

	openDatePickerDisplay() {
		this.DatePickerDisplay.show();
	}
}

export default DatePickerDisplay;
